import { useEffect, useState } from 'react';

import { fetchQuery, graphql, useRelayEnvironment } from 'react-relay';

import { useErrorAnalytics } from '../analytics/use-error-analytics';
import type { useGetStandardStorageHardLimitDataQuery } from './__generated__/useGetStandardStorageHardLimitDataQuery.graphql';
import { type EditionAwarenessContextType, useEditionAwareness } from './edition-awareness-context';
import { getHamsKey } from './product-to-hams-key';

type Data = {
	experienceUrl: string | null | undefined;
	isAvailableToUser: boolean | null | undefined;
	isInCollection: EditionAwarenessContextType['isInCollection'];
};

type StorageHardLimitData = {
	data: Data | null;
	error: Error | null;
	loading: boolean;
};

/**
 * Fetches the product entitlement's COLLECTION relationships to work out collection membership.
 * The Premium change-offering capability rides on the same query so the Standard storage dispatch
 * can gate its near-limit band without a second round trip.
 *
 * Never add a `where` filter to `entitlement` here: the root query's filter is what nulls the
 * entitlement for paid Standard, and this hook relies on the unfiltered entitlement coming back.
 */
export function useGetStandardStorageHardLimitData(): StorageHardLimitData {
	const { cloudId, product, edition } = useEditionAwareness();
	const environment = useRelayEnvironment();
	const reportError = useErrorAnalytics({
		product,
		edition,
		id: 'use-get-standard-storage-hard-limit-data',
	});

	const [returnData, setReturnData] = useState<StorageHardLimitData>({
		data: null,
		loading: true,
		error: null,
	});

	useEffect(() => {
		fetchQuery<useGetStandardStorageHardLimitDataQuery>(
			environment,
			graphql`
				query useGetStandardStorageHardLimitDataQuery($cloudId: ID!, $productKey: String!) {
					tenantContexts(cloudIds: [$cloudId]) {
						entitlementInfo(hamsProductKey: $productKey) {
							... on CommerceEntitlementInfoCcp {
								entitlement {
									relatesFromEntitlements(relationshipTypes: "COLLECTION") {
										relationshipType
										entitlementId
									}
									experienceCapabilities {
										changeOfferingV2ToPremium: changeOfferingV2(offeringName: "Premium") {
											isAvailableToUser
											experienceUrl
										}
									}
								}
							}
						}
					}
				}
			`,
			{ cloudId, productKey: getHamsKey(product) },
		)
			.toPromise()
			.then((data) => {
				const entitlement = data?.tenantContexts?.[0]?.entitlementInfo?.entitlement;

				// Resolved before the relationships check so a returned capability survives null relationships
				const changeOfferingToPremium =
					entitlement?.experienceCapabilities?.changeOfferingV2ToPremium;
				const experienceUrl = changeOfferingToPremium?.experienceUrl;
				const isAvailableToUser = changeOfferingToPremium?.isAvailableToUser;

				const relationships = entitlement?.relatesFromEntitlements;

				if (!relationships) {
					return setReturnData({
						data: { experienceUrl, isAvailableToUser, isInCollection: 'UNKNOWN' },
						loading: false,
						error: null,
					});
				}

				const isInCollection = relationships.some(
					(relationship) =>
						relationship?.relationshipType === 'COLLECTION' && relationship.entitlementId,
				)
					? 'IN_COLLECTION'
					: 'NOT_IN_COLLECTION';

				return setReturnData({
					data: {
						experienceUrl,
						isAvailableToUser,
						isInCollection,
					},
					loading: false,
					error: null,
				});
			})
			.catch((e: unknown) => {
				const error = e instanceof Error ? e : new Error(String(e));
				reportError(error);
				setReturnData({ data: null, loading: false, error });
			});
	}, [cloudId, environment, product, reportError]);

	return returnData;
}
